// Shared Help content (Q&A) used by both:
//   • In-app HelpPage (`/app/help`) — supports deep-link "Show me where" targets.
//   • Public Help page (`/help`)    — read-only, public-styled, ignores targets.
//
// Keep answers short and plain-English. The `target` block is OPTIONAL and
// only used by the in-app version to deep-link with a spotlight pulse.

import {
  Calendar, Users, Wallet, Inbox, Lock, Sparkles, Shield, Baby,
  MessageSquare, FileText, Bell, CreditCard, Archive, AlertCircle,
  HandHelping,
} from "lucide-react";

export const HELP = [
  // --------------------------------------------------------------------- //
  {
    id: "getting-started",
    title: "Getting started",
    icon: Sparkles,
    items: [
      {
        q: "What is CoParent Console?",
        keywords: ["what is", "about", "overview", "intro"],
        a: "CoParent Console is a calm, structured place for separated or divorced parents to manage co-parenting together. You can plan and document every contact visit and handover, share a calendar, message, track expenses, keep a journal, store documents and manage child profiles — all with a complete time-stamped audit trail. It is designed for accountability, transparency and secure record keeping, especially in high-conflict situations.",
      },
      {
        q: "How does it work in practice?",
        keywords: ["how works", "flow", "first steps"],
        a: "1. Sign up and create your account. 2. Add a child. 3. Invite your co-parent for that child by sending them a per-child invite link. 4. Start scheduling and documenting contact visits, parenting time, messages, expenses and notes. Every action is time-stamped — nothing can be silently edited or deleted, and disputes preserve both records side-by-side.",
      },
      {
        q: "Primary parent vs co-parent",
        keywords: ["primary", "co-parent", "coparent", "roles", "difference"],
        a: "The primary parent is the account holder who creates the household. They can add children, invite co-parents, manage permissions and see every child. A co-parent is invited per child — they only see the children they have been granted access to and never see anything outside that scope.",
      },
      {
        q: "Create your account",
        keywords: ["sign up", "register", "create account"],
        a: "Visit the home page and tap 'Create your free account'. You'll need a name, email and password. There's no credit card required and you start on a 30-day free trial of the full feature set.",
      },
      {
        q: "Add your first child",
        keywords: ["add child", "first child", "child profile"],
        a: "Go to Children → Add child. A name is enough to start. You can fill in date of birth, school, medical details and emergency contacts later. Each child gets their own profile and their own access list.",
        target: { route: "/app/children", testid: "add-child-button", label: "Open Children" },
      },
      {
        q: "Invite a co-parent (per child)",
        keywords: ["invite", "co-parent", "coparent", "share", "per child"],
        a: "Open the child's profile and tap 'Invite co-parent'. A single secure invite link is generated for THAT child. Send it by email or message. Important: invites are per child, not household-wide. To share two children with the same co-parent, send the invite for each child (or pick multiple children when generating).",
        target: { route: "/app/children", testid: "add-child-button", label: "Open Children" },
      },
      {
        q: "What happens when a co-parent accepts an invite?",
        keywords: ["accept invite", "joined", "co-parent joins"],
        a: "They create their own account and are automatically connected to ONLY the children covered by that invite. They will see those children, the messages, calendar events, expenses, journal entries and documents that relate to them — and nothing else. The primary parent sees them appear in the child's access list straight away.",
      },
    ],
  },

  // --------------------------------------------------------------------- //
  {
    id: "roles",
    title: "User roles & permissions",
    icon: Shield,
    items: [
      {
        q: "What can the primary parent do?",
        keywords: ["primary parent", "owner", "permissions"],
        a: "The primary parent can: add and edit children, invite co-parents per child, view every child and every connected co-parent, approve or reject change requests, manage subscriptions, and use the master 'All children' view across messages, calendar, expenses, journal and documents.",
      },
      {
        q: "What can a co-parent do?",
        keywords: ["co-parent", "coparent", "limits", "what can"],
        a: "A co-parent can fully participate (send messages, propose events, log expenses, write journal entries, upload documents) but ONLY for the children they have been invited to. They cannot see other children, other co-parents they aren't connected to, or the primary parent's data outside their assigned scope.",
      },
      {
        q: "Per-child access — what does that really mean?",
        keywords: ["scope", "child access", "isolation", "rbac"],
        a: "Every record (message, event, expense, journal entry, document, request) is tagged with the children it relates to. A co-parent with access to Child A only ever sees records tagged with Child A. They will never see expenses, messages or events for Child B — even by guessing IDs or filtering.",
      },
      {
        q: "How does the 'All children' toggle work?",
        keywords: ["all children", "toggle", "master view", "filter"],
        a: "For the primary parent, 'All children' is a true master view across every child. For a co-parent, 'All children' is a master view across only the children they have been invited to — never more. Switching it on never reveals data from a child they don't have access to.",
      },
      {
        q: "Can a co-parent see other co-parents?",
        keywords: ["see other co-parents", "other parents", "other coparent"],
        a: "Only co-parents who share at least one child with them. They never see the primary parent's full co-parent list. The aim is to keep each co-parenting relationship private to the people it actually involves.",
      },
      {
        q: "Can the primary parent revoke access?",
        keywords: ["revoke", "remove co-parent", "remove access", "kick"],
        a: "Yes. Open the child's profile → access list → revoke for any co-parent. They lose access to that child immediately. If they had access to other children too, those remain unchanged.",
      },
    ],
  },

  // --------------------------------------------------------------------- //
  {
    id: "children",
    title: "Managing children",
    icon: Baby,
    items: [
      {
        q: "Add a child",
        keywords: ["add child", "new child", "child profile"],
        a: "Children → Add child. Add a name to start. You can fill in date of birth, photo colour, school, medical info, routines and emergency contacts later.",
        target: { route: "/app/children", testid: "add-child-button", label: "Open Children" },
      },
      {
        q: "Edit child information",
        keywords: ["edit child", "update child", "change details"],
        a: "Open the child's profile and tap edit on any section (medical, school, routines, emergency contact). The primary parent can save changes directly. A co-parent's edits go through a change-request flow for the primary parent to approve.",
      },
      {
        q: "Medical, school and key details",
        keywords: ["medical", "school", "allergies", "emergency"],
        a: "Each child profile holds: GP and dentist contacts, allergies, medications, conditions, school name and contacts, regular routines (sleep, meals, activities) and emergency contacts. Both parents see this — it's meant to be the single source of truth.",
      },
      {
        q: "How do change requests work?",
        keywords: ["change request", "propose", "approval", "edit by co-parent"],
        a: "When a co-parent edits a child field, the change is saved as a 'proposal' in the change-request queue rather than written directly. The primary parent gets a notification, reviews the old vs proposed value, and approves or rejects. Approved changes are then applied to the record. Nothing changes silently.",
      },
      {
        q: "Why doesn't my edit save straight away?",
        keywords: ["edit not saving", "approval needed", "pending"],
        a: "If you're a co-parent, your edits are queued as change requests for the primary parent to approve. You'll see a 'Pending approval' label until they're actioned. The primary parent saves directly with no approval step.",
      },
    ],
  },

  // --------------------------------------------------------------------- //
  {
    id: "calendar",
    title: "Calendar & parenting schedules",
    icon: Calendar,
    items: [
      {
        q: "Create a parenting schedule",
        keywords: ["custody schedule", "parenting schedule", "alternating week"],
        a: "Calendar → Edit schedule. Choose an alternating-week pattern (e.g. week-on, week-off) or a custom-weekly pattern (e.g. Mon/Tue with parent A, Wed–Sun with parent B). Custody days are tinted by parent across the calendar.",
        target: { route: "/app/calendar", testid: "edit-parenting-schedule-btn", label: "Open the schedule editor" },
      },
      {
        q: "Handover times",
        keywords: ["handover", "pickup", "drop-off", "exchange"],
        a: "Within the schedule editor you can set the time custody changes hands each day. For a detailed record of an individual handover — who collected, when, what was observed — use Contact Visits → New contact visit instead. Contact Visits also appear on the Calendar as teal 'CV' chips for easy reference.",
      },
      {
        q: "Recurring events",
        keywords: ["recurring", "repeat", "weekly", "every"],
        a: "When adding an event, choose a frequency (daily, weekly, monthly) and an end rule (no end, on a date, after N times). When editing a recurring event later you'll be asked whether the change applies to just this one, this and future, or the whole series.",
        target: { route: "/app/calendar", testid: "add-event-button", label: "Open the event composer" },
      },
      {
        q: "Request a schedule change",
        keywords: ["change schedule", "swap", "override", "ask change"],
        a: "Use Requests → New request, pick the child it relates to, describe the change (e.g. 'swap this Saturday') and submit. The other parent sees it in their inbox. Nothing changes on the calendar until they accept.",
        target: { route: "/app/requests", testid: "new-request-button", label: "Open Requests" },
      },
      {
        q: "Private vs shared events",
        keywords: ["private event", "shared event", "visibility"],
        a: "By default, events are shared so both parents see them. You can flip an event to private if it's only relevant to your time with the child. Shared events show on both parents' calendars; private ones never do.",
      },
      {
        q: "Reminders & notifications",
        keywords: ["reminder", "notification", "alert"],
        a: "Each event can have a reminder (e.g. 1 hour before, 1 day before). Reminders are delivered as push or in-app notifications based on your preferences in Settings → Notifications.",
      },
    ],
  },

  // --------------------------------------------------------------------- //
  {
    id: "contact-visits",
    title: "Contact Visits",
    icon: HandHelping,
    items: [
      {
        q: "What are Contact Visits?",
        keywords: ["contact visits", "handover", "supervised", "visit"],
        a: "Contact Visits are a complete, secure record of every time a child spends time with a parent — whether it is parenting time, a handover, a supervised visit, a school collection, a video call or an overnight stay. Each visit can be planned ahead, documented as it happens, verified by both parents, and preserved with a full audit trail.",
      },
      {
        q: "How is a Contact Visit different from a Calendar event?",
        keywords: ["difference", "vs calendar", "vs event"],
        a: "Calendar events are reminders about what is planned. Contact Visits are the formal record of what actually happened: arrival and departure times, the people present, observations, supporting records, verification by both parents, and a unique record reference. Contact Visits also appear on the main Calendar as read-only teal 'CV' chips so you have a single view of everything.",
      },
      {
        q: "What types of visits can I record?",
        keywords: ["types", "kinds", "categories"],
        a: "Parenting Time, Handover, Supervised Contact, Contact Centre, School Collection, School Drop-off, Overnight Contact, Third-party Handover, Holiday Contact, Telephone, Video Call, Special Occasion and Other. The platform tracks each type separately so reports remain accurate.",
      },
      {
        q: "Can I record supervised contact?",
        keywords: ["supervised", "supervisor", "centre"],
        a: "Yes. Pick 'Supervised Contact' or 'Contact Centre' as the visit type and add the supervisor or facilitator to the Participants section. From Iteration 122 onwards, supervisors will be able to complete and digitally sign the record themselves through a secure facilitator portal.",
      },
      {
        q: "Can I record school collections and drop-offs?",
        keywords: ["school", "collection", "dropoff", "pickup"],
        a: "Yes. Use the 'School pickup' or 'School drop-off' visit types. These short, frequent contacts are first-class records, with their own record reference and audit trail just like any other visit.",
      },
      {
        q: "Can a third-party supervisor record a visit?",
        keywords: ["third party", "supervisor", "social worker", "facilitator"],
        a: "Yes — from Iteration 122. Parents can invite a supervisor, contact centre, social worker, mediator or Guardian ad Litem via a time-limited secure link. The third party can complete the factual record, upload observations and digitally sign without seeing either parent's private notes.",
      },
      {
        q: "Can a mediator or social worker verify a visit?",
        keywords: ["mediator", "social worker", "verify", "professional"],
        a: "When a professional facilitator (mediator, social worker, contact centre or Guardian ad Litem) is invited to a visit, their submission counts as an independent verification. The visit status updates to 'Verified by professional' to distinguish it from parent-only mutual confirmations.",
      },
      {
        q: "Who can see Contact Visit records?",
        keywords: ["visibility", "permissions", "who sees", "private"],
        a: "Shared factual details (date, time, location, status, record reference, participants and verification) are visible to both parents in the household. Private notes are visible only to their author. Third-party observations are visible to parents and the invited facilitator. Mediators, solicitors, Guardians ad Litem and court-appointed professionals only gain access if explicitly invited.",
      },
      {
        q: "Are my personal notes shared?",
        keywords: ["private notes", "author", "encrypted"],
        a: "No. Private notes are encrypted at rest (AES-GCM) and visible only to their author. They are never returned to the other parent or to any third party. If you want both parents to see a note, use a 'Shared factual' note instead.",
      },
      {
        q: "What happens if parents disagree about a visit?",
        keywords: ["dispute", "disagree", "conflict"],
        a: "If one parent confirms a visit and the other disputes it, the status changes to 'Disputed'. Both records are preserved side-by-side — the platform never overwrites, deletes or decides between them. The original submissions become immutable, and a dispute-response note category becomes available for additional commentary.",
      },
      {
        q: "Can Contact Visits be included in Court Bundles?",
        keywords: ["court bundle", "bundle", "court", "pdf"],
        a: "Yes. From Iteration 121.3 onwards, Contact Visit timelines, record references, participant lists and facilitator reports can be injected into the PDF Court Bundle. Every entry carries its unique record reference so it can be cited unambiguously in correspondence and proceedings.",
      },
      {
        q: "Are Contact Visit records time-stamped?",
        keywords: ["timestamp", "time-stamp", "audit"],
        a: "Yes — comprehensively. Each visit captures the scheduled and actual start/end times, the device that submitted them, who confirmed or disputed, every amendment, every note and every view. The complete audit trail is available in the visit's 'Audit trail' tab and is immutable once written.",
      },
      {
        q: "Can Contact Visits include supporting documents or photographs?",
        keywords: ["records", "photos", "documents", "attachments"],
        a: "From Iteration 121.4 onwards. Documents and photographs can be attached to a visit, are virus-scanned (ClamAV) on upload, and live in the visit's 'Records' tab alongside the timeline, participants and version history.",
      },
      {
        q: "Are Contact Visits suitable for family law proceedings?",
        keywords: ["court", "legal", "proceedings"],
        a: "They are designed to be. Every record carries a unique record reference (CV-YYYY-NNNNNN), a complete tamper-evident audit trail, AES-GCM encryption of sensitive fields and a verification model that preserves both parents' positions rather than determining outcomes. The Trust Centre details the underlying ISO 27001-aligned controls.",
      },
    ],
  },

  // --------------------------------------------------------------------- //
  {
    id: "expenses",
    title: "Expenses & payments",
    icon: Wallet,
    items: [
      {
        q: "Add an expense",
        keywords: ["add expense", "new expense", "log expense"],
        a: "Expenses → Add expense. Enter the amount, date, category, who paid it, which child(ren) it relates to and the split. Attach a receipt image if you have one.",
        target: { route: "/app/expenses", testid: "add-expense-button", label: "Open Expenses" },
      },
      {
        q: "Assign to one or multiple children",
        keywords: ["multiple children", "assign child", "shared expense"],
        a: "When adding an expense you can tag one child or several. The expense will only be visible to parents who have access to at least one of those children — and the split is shared only between parents linked to those children.",
      },
      {
        q: "Custom splits (not just 50/50)",
        keywords: ["split", "custom", "percentage", "fixed amount"],
        a: "Each expense supports three split types: 50/50, custom percentages (e.g. 70/30), or fixed amounts per parent. The system computes who paid upfront and who owes what back.",
      },
      {
        q: "How are reimbursements calculated?",
        keywords: ["reimbursement", "owed", "balance", "how calculated"],
        a: "For each expense: paid_by parent paid the upfront cost; the split determines each parent's share. The difference between what someone paid and what they owe becomes the outstanding balance. Settlements reduce that balance.",
      },
      {
        q: "Record a payment received",
        keywords: ["record payment", "got paid", "reimburse received"],
        a: "Open the outstanding expense → 'Record payment'. Enter the amount and date. Partial payments are supported. Once fully reimbursed, the expense is auto-marked settled and a settlement receipt is generated.",
        target: { route: "/app/expenses", testid: "settle-up-button", label: "Open Settle up" },
      },
      {
        q: "Mark several expenses as settled",
        keywords: ["settle up", "bulk settle", "settle multiple"],
        a: "Use 'Settle up' on the Expenses page. Pick which outstanding expenses are being cleared and the amount paid. A single settlement receipt PDF is generated covering them all.",
        target: { route: "/app/expenses", testid: "settle-up-button", label: "Open Settle up" },
      },
      {
        q: "Receipts & 'no receipt' indicator",
        keywords: ["receipt", "no receipt", "attach receipt"],
        a: "Attach a photo or PDF receipt to any expense. Expenses without a receipt show a clear 'No receipt added' label so it's obvious in audits. PDF exports include a Receipt Appendix with every receipt embedded inline so the document is court-ready on its own.",
      },
      {
        q: "Why can't I see my co-parent's other expenses?",
        keywords: ["can't see expenses", "missing expenses", "hidden"],
        a: "Co-parents only see expenses tagged with children they have access to. If an expense is tagged only with a child you aren't connected to, it's invisible to you by design. The 'All children' view never breaks this — it only spans your accessible children.",
      },
    ],
  },

  // --------------------------------------------------------------------- //
  {
    id: "requests",
    title: "Requests",
    icon: Inbox,
    items: [
      {
        q: "Send a request",
        keywords: ["new request", "ask", "permission", "swap"],
        a: "Requests → New request. Pick the child it relates to, choose a request type (schedule change, holiday, other), add a clear title and notes, and submit.",
        target: { route: "/app/requests", testid: "new-request-button", label: "Open Requests" },
      },
      {
        q: "Selecting which child the request is for",
        keywords: ["which child", "tag child", "request scope"],
        a: "Every request is tagged to one or more children. The other parent will only see it if they share that child with you — same per-child rule as everywhere else.",
      },
      {
        q: "Approval flow",
        keywords: ["accept", "decline", "approve", "reject", "respond"],
        a: "The other parent sees the request in their Inbox with full details and timestamps. They can Accept or Decline. Both actions are written to the audit trail. Nothing changes (calendar, schedule, expense) until they accept.",
      },
      {
        q: "Status meanings",
        keywords: ["pending", "accepted", "declined", "cancelled"],
        a: "Pending — awaiting a response. Accepted — both parties agree. Declined — turned down (you can submit a fresh one). Cancelled — the requester withdrew.",
      },
      {
        q: "Where do I see requests waiting on me?",
        keywords: ["inbox", "waiting", "pending requests"],
        a: "Your Dashboard shows a 'Requests waiting on you' card. Requests → Inbox lists them in detail. You'll also get a notification when one is created (subject to your notification preferences).",
        target: { route: "/app/requests", testid: "requests-tab-inbox", label: "Open the Inbox" },
      },
    ],
  },

  // --------------------------------------------------------------------- //
  {
    id: "messages",
    title: "Messages",
    icon: MessageSquare,
    items: [
      {
        q: "Send a message",
        keywords: ["send message", "write", "chat"],
        a: "Messages → write your message and send. Messages are time-stamped and cannot be edited or deleted once sent — that's deliberate so the record is reliable.",
        target: { route: "/app/messages", testid: "messages-input", label: "Open Messages" },
      },
      {
        q: "Message notifications",
        keywords: ["message notification", "notify message"],
        a: "By default you're notified when your co-parent sends a new message. You can adjust this in Settings → Notifications, or pause notifications altogether (8h, 24h, 48h, 72h, or until you turn them back on).",
      },
      {
        q: "Flag a concerning message",
        keywords: ["flag", "concerning", "report message"],
        a: "Open a message → Flag. Add a short reason (e.g. 'aggressive language'). Flagged messages are highlighted in the thread and listed in the Flagged messages view for easy reference. Flagging never edits or deletes the original.",
      },
      {
        q: "Export message history",
        keywords: ["export messages", "pdf messages", "download messages"],
        a: "From Messages → Export, generate a PDF of the conversation including timestamps, read status and any flags. This is the same record format used in the Records bundle export.",
      },
      {
        q: "Why can't I edit or delete a message?",
        keywords: ["edit message", "delete message", "remove message"],
        a: "Messages are immutable on purpose. If a court, mediator or auditor ever needs to read the history, it has to be a faithful, untampered record. If you sent something by mistake, send a follow-up correction.",
      },
    ],
  },

  // --------------------------------------------------------------------- //
  {
    id: "documents-journal",
    title: "Documents & Journal",
    icon: FileText,
    items: [
      {
        q: "Upload a document",
        keywords: ["upload", "document", "file", "pdf", "image"],
        a: "Documents → Upload. Choose a file, give it a name, pick the folder and the child(ren) it relates to. Set it to private (only you) or shared (visible to co-parents linked to that child).",
        target: { route: "/app/documents", testid: "documents-upload-button", label: "Open Documents" },
      },
      {
        q: "Private vs shared",
        keywords: ["private", "shared", "visibility", "documents"],
        a: "Documents are private by default and you flip them to shared when ready. Legal documents are intentionally never auto-shared — they always start private and stay private unless you explicitly share them.",
      },
      {
        q: "Link a document to a child",
        keywords: ["link child", "tag child", "child documents"],
        a: "When uploading, pick one or more children to tag. The document only becomes visible to co-parents who share that child. Untagged documents are visible only to the primary parent.",
      },
      {
        q: "Write a journal entry",
        keywords: ["journal", "incident", "log", "note"],
        a: "Journal → New entry. Add a title, body, the child(ren) involved, and decide private or shared. Time-stamped on creation. Use it for incidents, behaviour notes, milestones — anything you'd want a calm record of.",
        target: { route: "/app/journal", testid: "new-journal-entry-button", label: "Open Journal" },
      },
      {
        q: "Link a journal entry to a calendar event",
        keywords: ["link event", "journal event", "incident at event"],
        a: "When writing an entry, pick a recent calendar event to link to (e.g. 'handover on Saturday'). The entry then shows up on that event's detail view, making it easy to follow context across calendar and journal.",
      },
      {
        q: "Export records as a bundle",
        keywords: ["export", "bundle", "pdf", "court", "records"],
        a: "Settings → Export → Records bundle. You'll get a single PDF with messages, events, expenses, journal entries and receipts, scoped to the children you have access to. Use it for mediators, solicitors or court.",
      },
      {
        q: "Why can't I see documents from other children?",
        keywords: ["missing documents", "documents not visible"],
        a: "Same per-child rule applies: documents tagged with a child you don't have access to are invisible to you. The Records bundle export is also scoped this way.",
      },
    ],
  },

  // --------------------------------------------------------------------- //
  {
    id: "notifications",
    title: "Notifications",
    icon: Bell,
    items: [
      {
        q: "Turn notifications on or off",
        keywords: ["notifications on", "notifications off", "preferences"],
        a: "Settings → Notifications. Toggle each type (messages, requests, calendar reminders, expense updates, child profile changes) independently. Changes take effect immediately.",
        target: { route: "/app/settings", testid: "pause-card", label: "Open Notification settings" },
      },
      {
        q: "Pause notifications",
        keywords: ["pause", "snooze", "quiet", "do not disturb"],
        a: "Settings → Notifications → Pause for 8h, 24h, 48h or 72h, or 'Until I turn it back on'. While paused, a banner reminds you. Tap Resume now to bring them back instantly.",
        target: { route: "/app/settings", testid: "pause-card", label: "Show me Pause" },
      },
      {
        q: "Types of notifications",
        keywords: ["types", "kinds", "what notifications"],
        a: "Five categories: new messages, new or updated requests, calendar event reminders, expense updates (added/settled), and child profile change requests. Each can be turned on or off independently.",
      },
      {
        q: "I'm not getting any notifications",
        keywords: ["no notifications", "missing", "not arriving"],
        a: "Check three things: (1) Settings → Notifications — is the type enabled? (2) Pause card — are notifications paused? (3) Your device or browser permissions — push notifications need OS-level permission. If all three are fine, contact support.",
      },
    ],
  },

  // --------------------------------------------------------------------- //
  {
    id: "billing",
    title: "Subscription & billing",
    icon: CreditCard,
    items: [
      {
        q: "How does the trial work?",
        keywords: ["trial", "free trial", "30 days"],
        a: "Every new account starts with a 30-day free trial with full access to every day-to-day feature — no card required, no storage limits. Exports, downloads, Court Bundle generation and external record sharing are paid-only capabilities from day one and unlock when you subscribe. You'll see the days remaining on your dashboard. When the trial ends, you have a 7-day read-only grace period, and the account is locked after that until you subscribe (your data is preserved indefinitely).",
      },
      {
        q: "Is the subscription per parent or per child?",
        keywords: ["per parent", "per child", "how billed"],
        a: "Per parent account, not per child. One paid subscription on your account covers all the children linked to it. Co-parents you invite manage their own subscription on their own account.",
      },
      {
        q: "What happens when my trial ends?",
        keywords: ["after trial", "grace period", "read-only", "locked", "expired"],
        a: "When your 30-day free trial ends without a subscription, your account moves to a 7-day read-only grace period. You can sign in, view every record, review Insights and browse Court Bundles and Documents — but you can't add new entries, send messages, log expenses, invite co-parents, export PDFs, download reports, generate a Court Bundle or share records externally. After the 7-day grace ends, the account is locked until you subscribe (sign-in still works and shows a 'Subscribe to regain access' prompt). Your data is preserved indefinitely — nothing is deleted.",
      },
      {
        q: "What does upgrading unlock?",
        keywords: ["upgrade", "paid", "premium", "features"],
        a: "Everything that's paid-only: exporting PDFs, downloading reports, generating a Court Bundle, exporting data and sharing records externally — plus, if you're in the grace or locked state, the ability to add new entries again. Everything else is already included in the 30-day free trial.",
        target: { route: "/app/upgrade", testid: "upgrade-cta", label: "Open Upgrade" },
      },
      {
        q: "Where do I see my billing status?",
        keywords: ["billing", "invoice", "payment", "card"],
        a: "Settings → Subscription. You'll find your current plan, renewal date, payment method and a downloadable invoice history.",
        target: { route: "/app/settings", testid: "subscription-card", label: "Open Subscription" },
      },
      {
        q: "How do I cancel?",
        keywords: ["cancel", "stop subscription", "end plan"],
        a: "Settings → Subscription → Cancel subscription. You'll keep paid access until the end of the current billing period, after which your account moves into a 7-day read-only grace period — sign in, view records, review Insights and browse Court Bundles — and is then locked until you subscribe again. Your data is preserved indefinitely and nothing is deleted.",
      },
    ],
  },

  // --------------------------------------------------------------------- //
  {
    id: "inactivity",
    title: "Account inactivity & data retention",
    icon: Archive,
    items: [
      {
        q: "What counts as 'inactive'?",
        keywords: ["inactive", "activity", "last login"],
        a: "The inactivity lifecycle is a legacy safeguard for read-only accounts sitting inside the 7-day grace window. Locked, paid, complimentary, admin and protected accounts are exempt — locked accounts are preserved indefinitely and nothing is deleted, regardless of how long you take to subscribe again.",
      },
      {
        q: "The full lifecycle",
        keywords: ["lifecycle", "warning", "archive", "delete"],
        a: "Only in scope for the 7-day grace window: Day 90 (hypothetical, since grace is only 7 days) would send a re-engagement email; Day 97 a final warning; Day 104 an archive. In practice, once your 7-day grace ends your account moves to locked and the inactivity lifecycle no longer applies. Locked accounts are preserved indefinitely.",
      },
      {
        q: "How do I keep my account active?",
        keywords: ["stay active", "prevent archive", "keep account"],
        a: "You don't need to do anything special once your account is locked — locked accounts are preserved indefinitely. During the 30-day trial and 7-day grace window, any meaningful action (viewing calendar, adding an expense) resets any inactivity counter.",
      },
      {
        q: "I tried to log in and got 'Your account was archived'",
        keywords: ["archived", "can't login", "account archived"],
        a: "Your account was archived after 104 days of inactivity. Within 30 days of archive you can restore it directly from the login screen — tap 'Restore my account' and you'll be back in with all your data intact.",
      },
      {
        q: "What happens after permanent deletion?",
        keywords: ["permanent deletion", "anonymise", "gdpr", "delete forever"],
        a: "If you don't restore within 30 days of archive, your personal data is permanently anonymised. Records that are shared with a co-parent (e.g. messages they received) are kept but stripped of your name and email so the other side's history stays intact.",
      },
      {
        q: "Can I restore after the 30-day window?",
        keywords: ["restore late", "after 30 days", "recover"],
        a: "Once anonymisation has run, restoration isn't possible — your personal record no longer exists. Sign in within the 30-day archive window if you want your account back.",
      },
    ],
  },

  // --------------------------------------------------------------------- //
  {
    id: "privacy",
    title: "Privacy & data access",
    icon: Lock,
    items: [
      {
        q: "Who sees what?",
        keywords: ["who sees", "visibility", "access", "permissions"],
        a: "Co-parents only see data tagged with children they have been invited to. The primary parent sees every child and every co-parent. Mediators (if invited) only see the areas you've granted them. Operator/admin accounts of CoParent Console never see your household data — only aggregate metrics.",
      },
      {
        q: "Can a co-parent see other co-parents?",
        keywords: ["other co-parents", "other parent profiles"],
        a: "Only co-parents who share at least one child with them. Two co-parents who have access to different children of yours never see each other.",
      },
      {
        q: "Can a co-parent see expenses or events for children they aren't connected to?",
        keywords: ["see other children", "leak", "scope"],
        a: "No — under any circumstances. This is enforced server-side and continuously checked by automated tests. The 'All children' toggle, exports, settlements, audit trails and PDFs all respect per-child access.",
      },
      {
        q: "Private vs shared records",
        keywords: ["private", "shared", "default"],
        a: "Calendar events default to shared. Journal entries and documents default to private and you choose when to share them. Legal documents are private by default and never auto-shared.",
      },
      {
        q: "Where can I see who has access to a child?",
        keywords: ["access list", "see access", "who has access"],
        a: "Open the child's profile. The access list shows every connected co-parent and their status (invited / active). The primary parent can revoke access from this screen at any time.",
        target: { route: "/app/children", testid: "add-child-button", label: "Open Children" },
      },
    ],
  },

  // --------------------------------------------------------------------- //
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    icon: AlertCircle,
    items: [
      {
        q: "I can't sign in",
        keywords: ["can't login", "login issue", "wrong password", "credentials"],
        a: "Check the email is right and try 'Forgot password' to reset. If you see 'Your account was archived', tap 'Restore my account' on the login screen. If you see 'permanently deleted', the account is past the restore window and can't be recovered.",
      },
      {
        q: "I'm not seeing children or data I expect",
        keywords: ["missing children", "missing data", "not seeing"],
        a: "If you're a co-parent: you only see children you were invited to. Ask the primary parent to send you an invite for the missing child. If you're the primary parent: check Children → did you save the profile? Check the 'All children' toggle is on.",
      },
      {
        q: "Notifications aren't appearing",
        keywords: ["no notifications", "missing notifications"],
        a: "Three checks: Settings → Notifications type is on; Pause card isn't paused; your browser/OS allows push notifications for this site. If still nothing, sign out and back in to refresh the device token.",
      },
      {
        q: "The app won't load or looks broken",
        keywords: ["not loading", "broken", "blank", "white screen"],
        a: "Hard-refresh the page (Ctrl/Cmd + Shift + R). If still broken, sign out, clear the site's storage from your browser settings, and sign back in. If the issue persists, email support with a screenshot and the time it happened.",
      },
      {
        q: "Something looks wrong with an expense balance",
        keywords: ["wrong balance", "wrong total", "expense math"],
        a: "Tap the expense to see the breakdown: paid_by, split type, each parent's share, and any payments recorded. If a payment is missing, add it via 'Record payment'. If the split looks wrong, edit the expense and choose the correct split type.",
      },
      {
        q: "An invite link isn't working for my co-parent",
        keywords: ["invite link", "invite broken", "invite expired"],
        a: "Invite links are single-use and time-limited. If it's been used or has expired, generate a new one from the child's profile and re-send.",
      },
    ],
  },
];
